import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { ISearchPage } from "@/types/ScmTypes";
import React, { useState, useEffect } from "react";

import {
    DEFAULT_FIRST_PAGE_NUM,
    DEFAULT_PAGE_SIZE,
} from "@/utils/constants";

import { IStockRealInventorySearch, IStockRealInventoryDetail, stockRealInventoryService, IInventoryTotal, IRecordsDetail, IRecordsFields } from "./realInventoryStatisticService"
import { transPgToPagination, formatNum } from "@/utils/utils";
import { sourceAppOrBranch } from "@/types/VrmSaDictEnums";
import useGlobalModel from "@/model/globalModel";
import { convertRes2Blob } from "@/utils/exportFile";
import { message } from "antd";
import { ISCMPurchaseDetailsTotal, purchaseDetailsService } from '../purchaseDetails/purchaseDetailsService';
import { roleColumnModel } from "@/utils/roleColumn";

export interface IMdBeSearchField {
    code?: string;
    name?: string;
    corporation?: string;
    phone?: string;
    address?: string;
    bank?: string;
    bankAccount?: string;
}


export const useStockRealInventoryModel = createModel(function () {
    const { shopAndOrg, setLoadingInfo } = useGlobalModel()
    const { getHideRoleColumns } = roleColumnModel();
    //切换站点清除缓存
    useEffect(() => {
        // setSelectedRowKeys([]);
        reset()
    }, [shopAndOrg.shopCode])
    const initSearchParam: IStockRealInventorySearch = { inventoryQuantityGT: 0 };
    const initDataSource: IStockRealInventoryDetail[] = [];
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const initDesc = {
        averageAmount: 0,
        type: 0,
        latestAmountTotal: 0,
        total: 0
    }
    //查询条件
    const [searchParam, setSearchParam] = useState<IStockRealInventorySearch>(initSearchParam);
    //列表数据源
    const [dataSource, setDataSource] = useState<IStockRealInventoryDetail[]>(initDataSource);
    //表格选中行Key值
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    //列表数据源分页参数
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [descTotal, setDescTotal] = useState<IInventoryTotal>(initDesc);
    //选中的数据统计展示的数据
    const [rowsDescTotal, setRowsDescTotal] = useState<IInventoryTotal>(initDesc);
    const [descRows, setDescRows] = useState<IStockRealInventoryDetail[]>([]);

    const [historyVisible, setHistoryVisible] = useState<boolean>(false)
    const [historyRow, setHistoryRow] = useState<IStockRealInventoryDetail>({})
    const [occupyStock, setOccupyStockModel] = useState<IStockRealInventoryDetail | null>(null)
    //移库配件
    const [relocationList, setRelocationList] = useState<IStockRealInventoryDetail[]>([])
    const [relocationVisible, setRelocationVisible] = useState<boolean>(false)
    //移库记录列表
    const [recordsList, setRecordsList] = useState<IRecordsDetail[]>([])
    //移库记录检索
    const [recordsSearch, setRecordsSearch] = useState<IRecordsFields>({})
    //移库记录显示隐藏
    const [recordsVisible, setRecordsVisible] = useState<boolean>(false)
    const initRealInventorys = { stockNum: 0 };
    const [realInventorys, setRealInventorys] = useState<ISCMPurchaseDetailsTotal>(initRealInventorys);
    useEffect(() => {
        let newRowsDescTotal = initDesc
        relocationList.forEach((item, index) => {
            let { inventoryNum, latestAmount, averageAmount } = item
            newRowsDescTotal.averageAmount += +formatNum(averageAmount)
            newRowsDescTotal.type += +1
            newRowsDescTotal.latestAmountTotal += +formatNum(latestAmount)
            newRowsDescTotal.total += inventoryNum as number
        })
        setRowsDescTotal(newRowsDescTotal)
    }, [relocationList])

    const updatePgCfg = (updateParam: TablePaginationConfig) => {
        setPgCfg({ ...pgCfg, ...updateParam })
    }
    const updateSearchParam = (updateParam: IStockRealInventorySearch) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }
    const resetSearchParam = () => {
        setSearchParam(initSearchParam);
        loadData(initPgCfg, {});
        setSelectedRowKeys([])
        setRelocationList([])
        // setSelectedRow({})
    }
    const reset = () => {
        setSearchParam(initSearchParam);
        setSelectedRowKeys([])
    }
    const loadData = async (page?: ISearchPage, searchValue: IStockRealInventorySearch = searchParam) => {
        setLoading(true)
        const shopParam = {
            shopType: searchValue.shopId !== undefined ? 1 : shopAndOrg.shopType,
            shopId: searchValue.shopId ?? shopAndOrg.shopId
        };
        const searchParams = {
            ...searchValue,
            occupy: searchValue.occupy === null ? undefined : searchValue.occupy,
            ...shopParam,
            ...sourceAppOrBranch,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.pageNum || page?.current) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await stockRealInventoryService.page(searchParams);
        let { list } = retData[0]
        let { retData: realInventorys } = await purchaseDetailsService.amount(searchParams);
        let { records, ...pg } = list
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
        setRealInventorys(realInventorys);
        setLoading(false)
    }
    const exportStockRealInventory = async (searchValue: IStockRealInventorySearch = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        const shopParam = {
            shopType: searchValue.shopId !== undefined ? 1 : shopAndOrg.shopType,
            shopId: searchValue.shopId ?? shopAndOrg.shopId
        };
        const searchParams = {
            ...searchValue,
            ...shopParam,
            id: selectedRowKeys.join(),
            hidden: getHideRoleColumns("stockStatic", "主单"),
        }
        try {
            let result = await stockRealInventoryService.export(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch {
            setLoadingInfo({ loadingVisible: false, txt: "" });
        }

    }
    //移库
    const relocation = async () => {
        let newRelocationList = relocationList.map(item => {
            let { materialCode, materialName, relocationNum, shelfCode, shelfCodeNew, shelfId, shelfIdNew, shopId, shopName, id, warehouseCode, warehouseCodeNew, warehouseName, warehouseNameNew } = item
            return { materialCode, materialName, relocationNum, shelfCode, shelfCodeNew, shelfId, shelfIdNew, shopId, shopName, stockId: id, warehouseCode, warehouseCodeNew, warehouseName, warehouseNameNew }
        })
        let { retData } = await stockRealInventoryService.relocation({ details: newRelocationList });
        return retData;
    }


    //移库记录查看
    const records = async () => {
        let params = {
            ...recordsSearch,
            "pageNum": 1,
            "pageSize": 10000,
            shopId: shopAndOrg.shopId
        }
        let { retData } = await stockRealInventoryService.records(params);
        let { records } = retData
        setRecordsList(records)
    }
    //移库还原
    const reduction = async (id: string) => {
        let { retData } = await stockRealInventoryService.reduction(id);
        if (retData) {
            message.success("还件成功")
            records()
        }
    }
    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        drawerVisible,
        initPgCfg,
        /***method***/
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        updatePgCfg,
        setDrawerVisible,
        descTotal, setDescTotal,
        exportStockRealInventory,
        historyVisible, setHistoryVisible,
        historyRow, setHistoryRow,
        loading, setLoading,
        occupyStock, setOccupyStockModel,
        relocationList, setRelocationList,
        relocationVisible, setRelocationVisible,
        relocation,
        recordsList, setRecordsList,
        records,
        recordsVisible, setRecordsVisible,
        recordsSearch, setRecordsSearch,
        reduction,
        rowsDescTotal, setRowsDescTotal,
        descRows, setDescRows,
        reset,
        realInventorys, setRealInventorys
    }
});